import React, { useEffect, useState } from 'react';
import PhotoSwipe from 'photoswipe';
import PhotoSwipeUI_Default from 'photoswipe/dist/photoswipe-ui-default';
import 'photoswipe/dist/photoswipe.css';
import 'photoswipe/dist/default-skin/default-skin.css';
import IssueReportForm from './IssueReportForm';

function ImageGallery({ images, farmId, user }) {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://your-api-gateway-url.amazonaws.com/prod';
  
  const [gallery, setGallery] = useState(null);
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [isGalleryClosing, setIsGalleryClosing] = useState(false);

  // Function to generate HTML for image caption
  const generateTitleHTML = (fileId, barn, set) => {
    return `
      <div class="text-center">
        <div class='p-1 text-lg'>Barn: <span id='barn'>${barn}</span></div>
        <div class='p-1 text-lg'>Set: <span id='set'>${set}</span></div>
        <div>
          <button 
            class='report-issue-btn bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded mt-4 font-medium' 
            data-image-id='${fileId}'
          >
            Report Issue
          </button>
        </div>
      </div>
    `;
  };
  
  // Function to handle report button clicks
  const handleReportButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event from propagating to gallery
    
    // Get the image ID from the button's data attribute
    const imageId = e.currentTarget.getAttribute('data-image-id');
    
    // Properly close gallery before showing form
    handleReportIssue(imageId);
  };
  
  // Initialize PhotoSwipe
  const initPhotoSwipe = () => {
    const pswpElement = document.querySelectorAll('.pswp')[0];
    
    const items = images.map(image => ({
      src: `${API_BASE_URL}/images?fileId=${image.fileId}`,
      w: 1067,
      h: 491,
      title: generateTitleHTML(image.fileId, image.barn, image.set)
    }));
    
    const options = {
      history: false,
      focus: false,
      tapToToggleControls: false,
      showAnimationDuration: 0,
      hideAnimationDuration: 0,
      addCaptionHTMLFn: (item, captionEl) => {
        if(!item.title) {
          captionEl.children[0].innerHTML = '';
          return false;
        }
        captionEl.children[0].innerHTML = item.title;
        return true;
      }
    };
    
    // Create gallery
    const newGallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
    
    // Listen for when gallery is initialized and fully rendered
    newGallery.listen('initialZoomInEnd', () => {
      setTimeout(attachReportButtonEvents, 100);
    });
    
    // Listen for slide changes to reattach button handlers
    newGallery.listen('afterChange', attachReportButtonEvents);
    
    // Listen for when gallery is about to close
    newGallery.listen('close', () => {
      setIsGalleryClosing(true);
    });
    
    // Listen for when gallery is fully closed
    newGallery.listen('destroy', () => {
      if (selectedImageId && !showIssueForm) {
        // Only show the form if we're closing due to a report issue click
        // and not from a regular gallery close action
        setTimeout(() => {
          setShowIssueForm(true);
          setIsGalleryClosing(false);
        }, 300);
      } else {
        setIsGalleryClosing(false);
      }
    });
    
    newGallery.init();
    setGallery(newGallery);
  };
  
  // Helper function to attach event listeners to report buttons
  const attachReportButtonEvents = () => {
    const reportButtons = document.querySelectorAll('.report-issue-btn');
    reportButtons.forEach(btn => {
      // Remove any existing event listeners to prevent duplicates
      btn.removeEventListener('click', handleReportButtonClick);
      // Add the event listener
      btn.addEventListener('click', handleReportButtonClick);
    });
  };
  
  const handleReportIssue = (imageId) => {
    if (gallery) {
      // Store the imageId before closing the gallery
      setSelectedImageId(imageId);
      
      // Close the gallery - the destroy event will handle showing the form
      gallery.close();
    } else {
      // If gallery isn't open for some reason, just show the form
      setSelectedImageId(imageId);
      setShowIssueForm(true);
    }
  };
  
  const handleIssueSubmitted = () => {
    setShowIssueForm(false);
    setSelectedImageId(null);
    
    // Show success message
    alert('Issue reported successfully. Thank you for your feedback.');
  };
  
  const handleModalClose = () => {
    setShowIssueForm(false);
    setSelectedImageId(null);
  };
  
  useEffect(() => {
    // Cleanup function when component unmounts
    return () => {
      if (gallery) {
        gallery.close();
      }
    };
  }, [gallery]);
  
  return (
    <>
      <button
        className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded shadow"
        onClick={initPhotoSwipe}
      >
        View Images
      </button>
      
      {/* PhotoSwipe Template */}
      <div className="pswp" tabIndex="-1" role="dialog" aria-hidden="true">
        <div className="pswp__bg"></div>
        <div className="pswp__scroll-wrap">
          <div className="pswp__container">
            <div className="pswp__item"></div>
            <div className="pswp__item"></div>
            <div className="pswp__item"></div>
          </div>
          
          <div className="pswp__ui pswp__ui--hidden">
            <div className="pswp__top-bar">
              <div className="pswp__counter"></div>
              <button className="pswp__button pswp__button--close" title="Close (Esc)"></button>
              <button className="pswp__button pswp__button--fs" title="Toggle fullscreen"></button>
              <button className="pswp__button pswp__button--zoom" title="Zoom in/out"></button>
              
              <div className="pswp__preloader">
                <div className="pswp__preloader__icn">
                  <div className="pswp__preloader__cut">
                    <div className="pswp__preloader__donut"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-16 px-4 leading-tight text-center text-md text-white">
              Do not rely solely on these images to judge how you should enter and line up at a farm. 
              If you are entering a place you have never been to before, <b className="underline">always</b> get out of your truck and walk the farm to assess before entering.
            </div>
            
            <button className="pswp__button pswp__button--arrow--left" title="Previous (arrow left)"></button>
            <button className="pswp__button pswp__button--arrow--right" title="Next (arrow right)"></button>
            
            <div className="pswp__caption">
              <div className="pswp__caption__center"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Issue Report Form - Only show when gallery is fully closed */}
      {showIssueForm && !isGalleryClosing && (
        <IssueReportForm
          farmId={farmId}
          imageId={selectedImageId}
          user={user}
          onClose={handleModalClose}
          onSuccess={handleIssueSubmitted}
        />
      )}
    </>
  );
}

export default ImageGallery;